import dissolve from 'geojson-dissolve';
import { createSelector } from 'reselect';
import { dataSelector, rangeBreaksSelector } from 'selectors/indicator';
import { timeframeLatest } from 'selectors/indicator/timeframe';
import { allGeographiesInternational } from 'selectors/basic';
import { map as mapColors, noData } from 'config/colors';
import { worldMap } from 'config/mapConfig';
import {
  filter,
  get,
  getOr,
  groupBy,
  keys,
  map,
  mapValues,
  pipe,
  toPairs,
} from 'lodash/fp';
import { organizeData } from 'services/breakAndOrganizeData';

const getFeature = (features) =>
  ({ code }) =>
    features.find(({ properties }) => properties['iso-a2'] === code);

const getFeatures = (byId, group, features, data) => {
  if (-1 === ['region', 'subregion'].indexOf(group)) {
    return features;
  }

  return pipe([
    filter(({ code }) => (2 === code.length)),
    groupBy(group),
    mapValues((items) =>
      items
        .map(getFeature(features))
        .filter((x) => x)
    ),
    mapValues((x) => dissolve(...x)),
    toPairs,
    (pairs) => pairs.map(
      ([key, geometry]) => ({
        geometry,
        id: byId[key].code,
        properties: {
          'iso-a2': byId[key].code,
          name: byId[key].geography,
        },
        type: 'Feature',
      })
    ),
  ])(data);
};

const generateWorldMapData = (
  data,
  { byId },
  latest,
  rangeBreaks,
  { timeframe: activeTimeframe = latest.value, group } = {}
) => {
  const { mapData } = worldMap;
  const world = Object.keys(byId).find((id) => 'WORLD' === byId[id].code);

  const dataWithParents = data
    .filter(({ geography }) => world !== geography)
    .map(({ geography, ...rest }) => {
      const subregion = get('parent._id')(byId[geography]);
      const maybeRegion = getOr(subregion)('parent._id')(byId[subregion]);
      const region = world !== maybeRegion ? maybeRegion : subregion;
      return {
        ...rest,
        geography,
        code: byId[geography].code,
        subregion,
        region,
      };
    });

  const activeGeographies = group ?
    pipe([
      filter((datum) => datum[group]),
      groupBy(group),
      keys,
    ])(dataWithParents) :
    pipe([
      filter(({ code }) => 2 === code.length),
      map(({ geography }) => geography),
    ])(dataWithParents);

  const filteredData = dataWithParents
    .filter(({ timeframe: { value }, geography }) => (
      activeTimeframe === value &&
      -1 < activeGeographies.indexOf(geography)
    ));

  const { buckets, data: organizedData } =
    organizeData(rangeBreaks, filteredData, true);

  const mappedData = organizedData.map(({
    geography,
    bucket,
    value,
    ...state
  }) => ({
    ...state,
    geography,
    name: byId[geography].geography,
    bucket,
    value,
    color: 'n/a' !== value ? mapColors[bucket] : noData,
  }));

  const dataClasses = buckets.map(({ idx, min, max }) => ({
    color: mapColors[idx],
    from: max,
    to: min,
  }));

  return {
    ...worldMap,
    colorAxis: {
      dataClasses,
    },
    series: [{
      borderColor: '#FFFFFF',
      mapData: {
        ...mapData,
        features: getFeatures(byId, group, mapData.features, dataWithParents),
      },
      allAreas: false,
      data: mappedData,
      joinBy: ['iso-a2', 'code'],
      dataLabels: {
        enabled: false,
        format: '{point.name}',
      },
    }],
  };
};

export default createSelector(
  dataSelector,
  allGeographiesInternational,
  timeframeLatest,
  rangeBreaksSelector,
  (state, ownProps) => ownProps,
  generateWorldMapData
);



// WEBPACK FOOTER //
// ./app/selectors/indicator/maps/world.js